import { useMemo, useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box, Chip, Divider, Paper,
  Table, TableBody, TableCell, TableHead, TableRow,
  ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon      from '@mui/icons-material/Cancel'
import { Line }        from 'react-chartjs-2'

import {
  selectRows, selectDisplayFrom, selectDisplayTo,
  selectSelectedDayIdx, selectSelectedDrillType, selectVolumeThreshold,
  selectMedianWindow,
  setSelectedDayIdx, setSelectedDrillType,
} from '../../reducers/dataIntegrityReducer'
import {
  buildLookup, getUniqueDates, getDisplayDates, getDateReferenceWindow, getUniqueTypes,
  getUniqueSides, getUniqueVendors, vendorSideDetail,
} from '../../utils/dataIntegrityUtils'

const VENDOR_COLORS = {
  ERICSSON:      '#1565c0',
  HUAWEI:        '#e65100',
  NOKIA:         '#b71c1c',
  SAMSUNG:       '#6a1b9a',
  'NOT DEFINED': '#757575',
}
const SIDE_COLORS = ['#1565c0', '#e65100', '#2e7d32', '#6a1b9a', '#757575']
const TYPE_ORDER  = ['Site', 'Cell']

export default function DataIntegrityDrillDown() {
  const dispatch        = useDispatch()
  const rows            = useSelector(selectRows)
  const displayFrom     = useSelector(selectDisplayFrom)
  const displayTo       = useSelector(selectDisplayTo)
  const selectedDayIdx  = useSelector(selectSelectedDayIdx)
  const drillType       = useSelector(selectSelectedDrillType)
  const volumeThreshold = useSelector(selectVolumeThreshold)
  const medianWindow    = useSelector(selectMedianWindow)

  const [selectedVendor, setSelectedVendor] = useState(null)
  const [activeSides,    setActiveSides]    = useState([])

  const { lookup, dates, allDates, sides, vendors, availableTypes } = useMemo(() => ({
    lookup:         buildLookup(rows),
    dates:          getDisplayDates(rows, displayFrom, displayTo),
    allDates:       getUniqueDates(rows),
    sides:          getUniqueSides(rows),
    vendors:        getUniqueVendors(rows),
    availableTypes: TYPE_ORDER.filter(t => getUniqueTypes(rows).includes(t)),
  }), [rows, displayFrom, displayTo])

  const selectedDate = selectedDayIdx != null ? dates[selectedDayIdx] : null

  // Auto-select last day on load
  useEffect(() => {
    if (!rows.length || !dates.length) return
    dispatch(setSelectedDayIdx(dates.length - 1))
  }, [rows]) // eslint-disable-line

  // Presence rows
  const presenceRows = useMemo(() => {
    if (!selectedDate) return []
    return vendors.map(vendor => {
      const sideDetails = sides.map(side =>
        vendorSideDetail(lookup, drillType, side, vendor, selectedDate, allDates, volumeThreshold, medianWindow)
      ).filter(Boolean)
      if (!sideDetails.length) return null
      const allMissing = sideDetails.every(d => !d.hasData)
      const allOk      = sideDetails.every(d => d.hasData && !d.isLow)
      const hasLow     = sideDetails.some(d => d.hasData && d.isLow)
      const estado     = allMissing ? 'no data' : allOk ? 'ok' : hasLow && !sideDetails.some(d => !d.hasData) ? 'low volume' : 'partial'
      return { vendor, sideDetails, estado }
    }).filter(Boolean)
  }, [selectedDate, vendors, sides, lookup, drillType, allDates, volumeThreshold, medianWindow])

  // Auto-select ERICSSON (or first vendor) when presence rows change
  useEffect(() => {
    if (!presenceRows.length) return
    const ericsson = presenceRows.find(r => r.vendor === 'ERICSSON')
    setSelectedVendor(ericsson ? 'ERICSSON' : presenceRows[0].vendor)
    setActiveSides([])
  }, [selectedDate, drillType]) // eslint-disable-line

  // Sides for selected vendor
  const vendorSides = useMemo(() => {
    if (!selectedVendor) return []
    return sides.filter(side => {
      const byDate = lookup?.[drillType]?.[side]?.[selectedVendor]
      return byDate && allDates.some(d => (byDate[d] ?? 0) > 0)
    })
  }, [selectedVendor, sides, lookup, drillType, allDates])

  const effectiveSides = activeSides.length ? activeSides : vendorSides

  // Reference window shaded in the chart — anchored to the selected date's own
  // median window, trimmed to the part that falls before the visible period.
  // (The days inside the visible period are already plotted via `dates`; when
  // the selected date is near the end of the range, its 30-day window mostly
  // overlaps `dates` — without trimming, those days would be plotted twice
  // and the chart would appear to "reset" mid-way.)
  const refDates = useMemo(() => {
    if (!selectedDate) return []
    const window = getDateReferenceWindow(selectedDate, medianWindow)
    return displayFrom ? window.filter(d => d < displayFrom) : window
  }, [selectedDate, medianWindow, displayFrom])

  // Chart data: refDates + displayDates on x-axis
  const vendorChartData = useMemo(() => {
    if (!selectedVendor || !effectiveSides.length) return null

    const chartDates = [...refDates, ...dates]
    const refCount   = refDates.length

    const sideDatasets = effectiveSides.map((side, idx) => {
      const byDate  = lookup?.[drillType]?.[side]?.[selectedVendor] ?? {}
      const sideIdx = vendorSides.indexOf(side)
      const color   = SIDE_COLORS[sideIdx >= 0 ? sideIdx : idx] ?? '#888'
      return {
        label:           side,
        data:            chartDates.map(d => byDate[d] ?? 0),
        borderColor:     color,
        backgroundColor: color + '18',
        borderWidth: 2,
        fill: false,
        tension: 0.3,
        pointRadius: 1.5,
        pointHoverRadius: 4,
      }
    })

    const markerIdx  = refCount + selectedDayIdx
    const maxVal     = Math.max(0, ...sideDatasets.flatMap(ds => ds.data))
    const markerData = chartDates.map((_, i) => i === markerIdx ? maxVal : null)
    const markerDataset = {
      label: '__marker__', data: markerData,
      type: 'bar', backgroundColor: '#1565c015',
      borderColor: '#1565c0', borderWidth: 1,
      barThickness: 1, pointRadius: 0, hoverRadius: 0,
    }

    return { labels: chartDates, datasets: [...sideDatasets, markerDataset], refCount }
  }, [selectedVendor, effectiveSides, lookup, drillType, dates, refDates, selectedDayIdx, vendorSides])

  // Chart.js v2 plugin: reference area shading + separator + day marker
  const chartPlugin = useMemo(() => ({
    afterDraw(chart) {
      const ctx       = chart.ctx
      const area      = chart.chartArea
      const meta      = chart.getDatasetMeta(0)
      if (!meta?.data?.length) return

      const refCount = chart.config.data?.refCount ?? 0

      // Reference area shading
      if (refCount > 0 && meta.data[refCount - 1]) {
        const refEndX = meta.data[refCount - 1]._model.x
        ctx.save()
        ctx.fillStyle = 'rgba(0,0,0,0.05)'
        ctx.fillRect(area.left, area.top, refEndX - area.left + 4, area.bottom - area.top)
        ctx.restore()

        // Separator
        ctx.save()
        ctx.beginPath()
        ctx.setLineDash([4, 3])
        ctx.strokeStyle = '#bdbdbd'
        ctx.lineWidth = 1
        ctx.moveTo(refEndX + 4, area.top)
        ctx.lineTo(refEndX + 4, area.bottom)
        ctx.stroke()
        ctx.restore()
      }

      // Day marker
      const markerIdx = chart.config.options.lineAtIndex
      if (markerIdx != null && meta.data[markerIdx]) {
        const x = meta.data[markerIdx]._model.x
        ctx.save()
        ctx.beginPath()
        ctx.setLineDash([4, 3])
        ctx.strokeStyle = '#1565c0'
        ctx.lineWidth = 1.5
        ctx.moveTo(x, area.top)
        ctx.lineTo(x, area.bottom)
        ctx.stroke()
        ctx.restore()
      }
    },
  }), [])

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    tooltips: {
      mode: 'index', intersect: false,
      filter: item => item.datasetIndex !== (vendorChartData?.datasets?.length - 1),
      callbacks: {
        title: items => items[0]?.xLabel ?? '',
        label: (item, data) => {
          const ds = data.datasets[item.datasetIndex]
          if (ds.label === '__marker__') return null
          return ` ${ds.label}: ${Number(item.yLabel).toLocaleString()}`
        },
      },
    },
    legend: { display: false },
    lineAtIndex: vendorChartData ? (vendorChartData.refCount ?? 0) + selectedDayIdx : null,
    scales: {
      xAxes: [{ gridLines: { display: false }, ticks: { fontSize: 9, fontColor: '#9e9e9e', maxTicksLimit: 12, maxRotation: 0 } }],
      yAxes: [{ gridLines: { color: '#f0f0f0' }, ticks: { fontSize: 9, fontColor: '#9e9e9e', callback: v => v >= 1000 ? Math.round(v / 1000) + 'K' : v } }],
    },
  }), [selectedDayIdx, vendorChartData])

  const handleChartClick = (elems) => {
    if (!elems?.length || !vendorChartData) return
    const idx = elems[0]._index
    const refCount = vendorChartData.refCount ?? 0
    const displayIdx = idx - refCount
    if (displayIdx >= 0 && displayIdx < dates.length)
      dispatch(setSelectedDayIdx(displayIdx))
  }

  if (!rows.length) return null

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {selectedDate ? `Detail — ${selectedDate} · ${drillType}` : 'Loading...'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Data presence by vendor and side — click a vendor to see volume by side
          </Typography>
        </Box>
        {availableTypes.length > 1 && (
          <ToggleButtonGroup exclusive size="small" value={drillType}
            onChange={(_, v) => { if (v) dispatch(setSelectedDrillType(v)) }}
            sx={{ height: 28 }}>
            {availableTypes.map(t => (
              <ToggleButton key={t} value={t} sx={{ px: 2, fontSize: 11, textTransform: 'none' }}>{t}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}
      </Box>

      {/* Presence table */}
      {presenceRows.length > 0 && (
        <Table size="small" sx={{ mb: 0.5 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Vendor</TableCell>
              {sides.map(s => <TableCell key={s} align="center" sx={{ fontWeight: 600, fontSize: 12 }}>{s}</TableCell>)}
              <TableCell align="center" sx={{ fontWeight: 600, fontSize: 12 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {presenceRows.map(({ vendor, sideDetails, estado }) => {
              const isActive = selectedVendor === vendor
              return (
                <TableRow key={vendor}
                  onClick={() => { setSelectedVendor(isActive ? null : vendor); setActiveSides([]) }}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: isActive ? '#e3f2fd'
                      : estado === 'no data'    ? '#fdecea'
                      : estado === 'partial'    ? '#fff8e1'
                      : estado === 'low volume' ? '#fffde7'
                      : 'inherit',
                    '&:hover': { bgcolor: isActive ? '#e3f2fd' : 'action.hover' },
                    borderLeft: isActive ? `3px solid ${VENDOR_COLORS[vendor] ?? '#1565c0'}` : '3px solid transparent',
                  }}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: VENDOR_COLORS[vendor] ?? 'grey.400', flexShrink: 0 }} />
                      <Typography variant="body2">{vendor}</Typography>
                    </Box>
                  </TableCell>
                  {sides.map(side => {
                    const detail = sideDetails.find(d => d.side === side)
                    if (!detail) return (
                      <TableCell key={side} align="center">
                        <Tooltip title="Vendor does not operate in this side" arrow placement="top">
                          <Typography variant="caption" color="text.disabled" sx={{ cursor: 'help' }}>—</Typography>
                        </Tooltip>
                      </TableCell>
                    )
                    return (
                      <TableCell key={side} align="center">
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                          {detail.hasData
                            ? <CheckCircleIcon sx={{ fontSize: 16, color: detail.isLow ? 'warning.main' : 'success.main' }} />
                            : <CancelIcon sx={{ fontSize: 16, color: 'error.main' }} />}
                          {detail.hasData && (
                            <Typography variant="caption" color={detail.isLow ? 'warning.main' : 'text.disabled'} sx={{ fontSize: 9 }}>
                              {detail.total.toLocaleString()}{detail.isLow ? ` (med: ${detail.median.toLocaleString()})` : ''}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    )
                  })}
                  <TableCell align="center">
                    <Chip size="small" label={estado}
                      color={estado === 'no data' ? 'error' : estado === 'partial' ? 'warning' : estado === 'low volume' ? 'warning' : 'success'}
                      variant="outlined" sx={{ fontSize: 10, height: 20 }} />
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}

      <Divider sx={{ my: 1.5 }} />

      {/* Volume chart */}
      {selectedVendor && vendorChartData ? (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
            <Typography variant="caption" color="text.secondary">
              Volume by side — <strong>{selectedVendor}</strong> · {drillType}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              {vendorSides.map((side, idx) => {
                const color    = SIDE_COLORS[idx] ?? '#888'
                const isActive = activeSides.length === 0 || activeSides.includes(side)
                return (
                  <Box key={side}
                    onClick={() => setActiveSides(prev => prev.length === 1 && prev[0] === side ? [] : [side])}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 0.5,
                      cursor: 'pointer', px: 0.75, py: 0.25, borderRadius: 1,
                      border: '1px solid', borderColor: isActive ? color : 'divider',
                      bgcolor: isActive ? color + '15' : 'transparent',
                      transition: 'all .15s',
                      '&:hover': { bgcolor: color + '25' },
                    }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: isActive ? color : 'grey.300', flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ fontSize: 11, color: isActive ? 'text.primary' : 'text.disabled' }}>
                      {side}
                    </Typography>
                  </Box>
                )
              })}
            </Box>
          </Box>
          <Box sx={{ position: 'relative', height: 220 }}>
            <Line
              data={vendorChartData}
              options={chartOptions}
              plugins={[chartPlugin]}
              onElementsClick={handleChartClick}
            />
          </Box>
          {vendorChartData.refCount > 0 && (
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.75, fontStyle: 'italic' }}>
              Shaded area ({vendorChartData.refCount} days) — reference period used for median calculation only
            </Typography>
          )}
        </>
      ) : (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', py: 1 }}>
          Click a vendor to see volume by side over time
        </Typography>
      )}
    </Paper>
  )
}